import { useEffect, useRef, useState } from "react";
import { Control, useWatch } from "react-hook-form";

interface IUseDefaultInputFieldProps {
  name: string;
  control: Control<any>;
  prefix?: string;
  autofocus?: boolean;
  setValue: (name: string, value: any) => void;
}

export const useDefaultInputField = ({
  name,
  control,
  prefix = "",
  autofocus = false,
  setValue,
}: IUseDefaultInputFieldProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const fieldValue = useWatch({ name, control });

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onContainerClick = () => {
    if (ref.current) {
      ref.current.focus();
    }
  };

  // Consolidated prefix logic to prevent race conditions
  useEffect(() => {
    if (!prefix) return;

    const currentValue = fieldValue || "";
    const stringValue = String(currentValue);
    let newValue = stringValue;

    if (stringValue === "" || stringValue === null) {
      newValue = prefix;
    } else if (!stringValue.startsWith(prefix)) {
      const valueWithoutPrefix = stringValue.replace(
        new RegExp(`^${prefix}*`),
        ""
      );
      newValue = prefix + valueWithoutPrefix;
    } else if (stringValue.length < prefix.length) {
      newValue = prefix;
    }

    if (newValue !== stringValue) {
      requestAnimationFrame(() => {
        setValue(name, newValue);
      });
    }
  }, [fieldValue, prefix, setValue, name]);

  // Autofocus effect
  useEffect(() => {
    if (autofocus && ref.current) {
      const timer = setTimeout(() => {
        ref.current?.focus();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [autofocus]);

  // Prefix input handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: any) => void
  ) => {
    const newValue = e.target.value;

    if (prefix) {
      if (newValue.length < prefix.length || !newValue.startsWith(prefix)) {
        const valueAfterPrefix =
          newValue.length >= prefix.length ? newValue.slice(prefix.length) : "";
        onChange(prefix + valueAfterPrefix);
      } else {
        onChange(newValue);
      }
    } else {
      onChange(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (prefix && ref.current) {
      const input = ref.current;
      const { selectionStart } = input;

      if (
        (e.key === "Backspace" || e.key === "Delete") &&
        selectionStart !== null &&
        selectionStart < prefix.length
      ) {
        e.preventDefault();

        requestAnimationFrame(() => {
          if (input) {
            input.setSelectionRange(prefix.length, prefix.length);
          }
        });
      }
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (prefix && ref.current) {
      requestAnimationFrame(() => {
        const input = ref.current;
        if (
          input &&
          input.selectionStart !== null &&
          input.selectionStart < prefix.length
        ) {
          input.setSelectionRange(prefix.length, prefix.length);
        }
      });
    }
  };

  return {
    ref,
    isFocused,
    setIsFocused,
    onFocus,
    onBlur,
    onContainerClick,
    handleInputChange,
    handleKeyDown,
    handleClick,
  };
};
